import type { ReactNode } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { getInitials } from '@/lib/initials.util'
import { cn } from '@/lib/utils'

export interface UserAvatarInfoProps {
  name: string
  email: string
  imageUrl: string | null
  className?: string
  children?: ReactNode
}

/**
 * Displays user avatar with name and email info.
 * Used in sidebar user menu trigger and dropdown label.
 */
export const UserAvatarInfo: React.FC<UserAvatarInfoProps> = ({
  name,
  email,
  imageUrl,
  className,
  children,
}): ReactNode => {
  const initials = getInitials(name)

  return (
    <div
      className={cn('user-avatar-info flex w-full items-center gap-2 text-left text-sm', className)}
    >
      <Avatar className="h-8 w-8 rounded-lg">
        <AvatarImage src={imageUrl ?? undefined} alt={name} />
        <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-semibold">{name}</span>
        <span className="truncate text-xs text-muted-foreground">{email}</span>
      </div>
      {children}
    </div>
  )
}
